import { Box, Button, Container, Stack, Typography, useMediaQuery } from '@mui/material';
import { bannerData, servicesData } from 'src/resources/data/landingPageData';

import ChooseUsBanner from './chooseUsBanner';
import DiscountBanner from './discountBanner';
import FooterSection from '../footerSection';
import Navbar from 'src/components/navbar/navbar';
import React from 'react';
import { Routes } from 'src/utils/enums';
import ServiceCard from 'src/components/serviceCard';
import { ServiceType } from 'src/utils/types';
import SpecialOffers from './specialOffers';
import Testimonials from './testimonials';
import TypingEffect from 'src/components/typingEffect';
import backgroundImage from "../../resources/images/hero_img_bg.jpg";
import bannerBgImage from "../../resources/images/img_3.jpg"
import { content } from 'src/common/lang/en-US';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';

const LandingPage: React.FC = () => {
  const { heading, headingContent } = content.landingPage;

  const servicesRef = useRef<HTMLDivElement | null>(null);

  const xsScreen = useMediaQuery('(max-width:700px)');
  const smScreen = useMediaQuery('(max-width:900px)');

  const navigate = useNavigate();

  const handleSelectService = (service: ServiceType): void => {
    if (service.link) {
      navigate(`/${service.link}`)
    }
    else {
      navigate(Routes.SERVICES_PAGE)
    }
  }

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: "background.iceGray" }}>
      <Box sx={{ position: 'relative', height: '100vh' }}>

        {/* Background image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: "column",
            alignItems: 'center',
            color: '#fff',
            textAlign: 'center',
            background: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <Navbar />

          <Container sx={{ mt: 12, display: "flex", flexDirection: "column" }}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.3
                  }
                }
              }}
            >
              {/* Heading */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  visible: { opacity: 1, y: 0 }
                }}
                transition={{ duration: 1 }}
              >
                <Typography variant="h1_Thin" component="h1" gutterBottom>
                  {heading}
                </Typography>
              </motion.div>

              {/* Typing effect */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  visible: { opacity: 1, y: 0 }
                }}
                transition={{ duration: 1 }}
              >
                <TypingEffect
                  words={headingContent}
                  typographySX={{
                    color: "#ea728c",
                    fontSize: "4rem",
                  }}
                  varient='h1_Thin'
                />
              </motion.div>

              {/* Get Started Button */}
              <Stack justifyContent="center" direction="row" width="100%">
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 40 },
                    visible: { opacity: 1, y: 0 }
                  }}
                  transition={{ duration: 1 }}
                >
                  <Button
                    onClick={() => {
                      servicesRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    sx={{
                      backgroundColor: "#ea728c",
                      color: "#FFFFFF",
                      borderRadius: "30px",
                      px: "30px",
                      py: "18px",
                      mt: 4,
                      fontSize: "14px",
                      width: "10rem"
                    }}
                  >
                    Get Started
                  </Button>
                </motion.div>
              </Stack>
            </motion.div>
          </Container>
        </Box>

        {/* Banner Box */}
        <Box alignItems="center" flexDirection="row" justifyContent="center" display="flex" width="100%" top="90%" position="absolute" zIndex={20000}>
          <Box p="30px" boxShadow="0 15px 30px 0 rgba(0, 0, 0, 0.05)" bgcolor="#fff" width="90%">
            <Box
              display="grid"
              gap={4}
              width="100%"
              gridTemplateColumns={{
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(4, 1fr)',
                md: 'repeat(4, 1fr)'
              }}
            >
              {bannerData.map((banner, index) => (
                <Stack width="100%" key={index} gap={2} direction="row" alignItems="center">
                  {React.createElement(banner.icon, {
                    sx: { color: "#ea728c", fontSize: "3rem" }
                  })}
                  <Stack direction="column">
                    <Typography variant="h6_Thin">
                      {banner.title}
                    </Typography>
                    <Typography variant="body2" color="#b3b3b3">
                      {banner.subTitle}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Services Section */}
      <Box ref={servicesRef} pb={10} pt={(smScreen || xsScreen) ? 40 : 10} sx={{ px: 2, backgroundColor: '#fafafa', zIndex: 2, position: 'relative', bgcolor: "#ffffff" }}>
        <Box px={{ xs: 0, sm: "3.2rem" }} py="3.2rem" bgcolor="#fff" width="100%">
          <Box
            display="grid"
            gap={3}
            width="100%"
            gridTemplateColumns={{
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)'
            }}
          >
            {servicesData(true).slice(0, 4).map((service, index) => (
              <ServiceCard
                subTitle={service.subTitle}
                onClick={() => handleSelectService(service)}
                key={index}
                image={service.image}
                title={service.title}
                description={service.description}
                buttonName={service.buttonName}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Why choose us section component */}
      <ChooseUsBanner />

      {/* Special offers section component */}
      <SpecialOffers />

      {/* Customer Reviews */}
      <Testimonials />

      {/* discount banner component */}
      <Box
        sx={{
          position: 'relative',
          height: '60vh',
          backgroundImage: `url(${bannerBgImage})`,
          backgroundSize: 'cover',
          backgroundAttachment: 'fixed',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(234, 114, 140, 0.9)',
            zIndex: 1,
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <DiscountBanner />
        </Box>
      </Box>

      <FooterSection />
    </Box>
  );
};

export default LandingPage;
